import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Clock,
  Package,
  AlertTriangle,
} from 'lucide-react';

export default function Dashboard() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { currentOutlet } = useOutlet();
  const { formatCurrency } = useCurrency();
  const [stats, setStats] = useState({
    todaySales: 0,
    todayOrders: 0,
    pendingOrders: 0,
    lowStockItems: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentOutlet?.id) {
      fetchDashboardData();
    }
  }, [currentOutlet?.id]);

  const fetchDashboardData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch today's orders
      const ordersResp = await api.get('/orders', {
        params: { outlet_id: currentOutlet.id, date: today },
      });
      const orders = ordersResp.data;
      
      const completedOrders = orders.filter(o => o.status === 'COMPLETED');
      const pendingOrders = orders.filter(o => !['COMPLETED', 'CANCELLED'].includes(o.status));
      
      // Fetch low stock
      const lowStockResp = await api.get('/inventory/low-stock', {
        params: { outlet_id: currentOutlet.id },
      });
      
      // Fetch top items for the month
      const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
        .toISOString().split('T')[0];
      const topItemsResp = await api.get('/reports/top-items', {
        params: { outlet_id: currentOutlet.id, start_date: startOfMonth, end_date: today, limit: 5 },
      });
      
      setStats({
        todaySales: completedOrders.reduce((sum, o) => sum + o.total, 0),
        todayOrders: orders.length,
        pendingOrders: pendingOrders.length,
        lowStockItems: lowStockResp.data.length,
      });
      
      setRecentOrders(orders.slice(0, 5));
      setTopItems(topItemsResp.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      CONFIRMED: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      PREPARING: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
      READY: 'bg-green-500/10 text-green-600 dark:text-green-400',
      SERVED: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      COMPLETED: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
      CANCELLED: 'bg-red-500/10 text-red-600 dark:text-red-400',
    };
    return colors[status] || colors.PENDING;
  };

  if (!currentOutlet) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">{t('dashboard.select_outlet_first')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('dashboard.title')}</h1>
        <p className="text-muted-foreground">
          {t('dashboard.welcome', { name: user?.name })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.todays_sales')}
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono" data-testid="today-sales">
              {formatCurrency(stats.todaySales)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.orders_today')}
            </CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono" data-testid="today-orders">
              {stats.todayOrders}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.pending_orders')}
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-orange-500" data-testid="pending-orders">
              {stats.pendingOrders}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('dashboard.low_stock_alerts')}
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold font-mono ${stats.lowStockItems > 0 ? 'text-red-500' : ''}`} data-testid="low-stock">
              {stats.lowStockItems}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Orders */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              {t('dashboard.recent_orders')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentOrders.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('dashboard.no_orders_today')}</p>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-3 rounded-md bg-muted/50"
                    data-testid={`order-${order.order_number}`}
                  >
                    <div>
                      <p className="font-medium font-mono text-sm">#{order.order_number}</p>
                      <p className="text-xs text-muted-foreground">
                        {order.order_type} • {order.items?.length || 0} {t('dashboard.items')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm font-medium">{formatCurrency(order.total)}</p>
                      <Badge variant="outline" className={getStatusColor(order.status)}>
                        {order.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Selling Items */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {t('dashboard.top_selling_this_month')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topItems.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('dashboard.no_sales_data')}</p>
            ) : (
              <div className="space-y-3">
                {topItems.map((item, index) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between p-3 rounded-md bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-sm">
                        {index + 1}
                      </span>
                      <span className="font-medium">{item.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm">{item.quantity} {t('dashboard.sold')}</p>
                      <p className="text-xs text-muted-foreground">{formatCurrency(item.revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
